import { AxiosResponse } from 'axios';
import { isValidWorkyServerResponseWithData } from '../common/WorkyAPIUtils';
import {
    RemoteExecutionServiceImpl,
    RemoteGETCallInputObject,
    ServerResponse,
} from '../remoteExecution/RemoteExecutionServiceImpl';
import type { AuthClientConfigResponse } from './WorkyAuthClientConfigInterfaces';

const AUTH_CLIENT_CONFIG_API_PATH: string = 'auth/client-config/';
const AUTH_CLIENT_CONFIG_SESSION_CACHE_KEY: string = 'worky_auth_client_config_use_custom_auth_ui';

let cachedRemoteCallObject: ReturnType<typeof RemoteExecutionServiceImpl> | null = null;

const getRemoteCallObject = (): ReturnType<typeof RemoteExecutionServiceImpl> => {
    if (cachedRemoteCallObject === null) {
        cachedRemoteCallObject = RemoteExecutionServiceImpl();
    }
    return cachedRemoteCallObject;
};

let cachedUseCustomAuthUi: boolean | null = null;
let authClientConfigLoadPromise: Promise<boolean> | null = null;

const readCachedUseCustomAuthUiFromSession = (): boolean | null => {
    try {
        const cachedValue: string | null = sessionStorage.getItem(AUTH_CLIENT_CONFIG_SESSION_CACHE_KEY);
        if (cachedValue === 'true') {
            return true;
        }
        if (cachedValue === 'false') {
            return false;
        }
        return null;
    } catch {
        return null;
    }
};

const writeCachedUseCustomAuthUiToSession = (useCustomAuthUi: boolean): void => {
    try {
        sessionStorage.setItem(AUTH_CLIENT_CONFIG_SESSION_CACHE_KEY, useCustomAuthUi ? 'true' : 'false');
    } catch {
        // ignore session storage failures
    }
};

const fetchAuthClientConfigFromServer = async (): Promise<boolean> => {
    // Temporary short-circuit: skip remote config fetch and force custom auth UI on.
    // Restore the remote call below when server-driven config is needed again.
    const useCustomAuthUi: boolean = true;
    cachedUseCustomAuthUi = useCustomAuthUi;
    writeCachedUseCustomAuthUiToSession(useCustomAuthUi);
    return useCustomAuthUi;

    // const getCallInput: RemoteGETCallInputObject = {
    //     apiUrl: AUTH_CLIENT_CONFIG_API_PATH,
    //     headers: {},
    //     queryParams: {},
    // };
    //
    // try {
    //     const apiResponse: AxiosResponse<ServerResponse<AuthClientConfigResponse, Error>> =
    //         await getRemoteCallObject().remoteGETCallV2<ServerResponse<AuthClientConfigResponse, Error>>(getCallInput);
    //
    //     if (isValidWorkyServerResponseWithData(apiResponse)) {
    //         const useCustomAuthUi: boolean = apiResponse.data.data.useCustomAuthUi;
    //         cachedUseCustomAuthUi = useCustomAuthUi;
    //         writeCachedUseCustomAuthUiToSession(useCustomAuthUi);
    //         return useCustomAuthUi;
    //     }
    // } catch (error) {
    //     console.error('Failed to fetch auth client config, defaulting to Hosted UI', error);
    // }
    //
    // cachedUseCustomAuthUi = false;
    // writeCachedUseCustomAuthUiToSession(false);
    // return false;
};

const FORCE_CUSTOM_AUTH_UI: boolean = true;

const preloadAuthClientConfig = (): Promise<boolean> => {
    // Temporary: ignore stale in-memory / session cache so a previous Hosted-UI
    // false value cannot block the custom login flow.
    if (FORCE_CUSTOM_AUTH_UI) {
        return fetchAuthClientConfigFromServer();
    }

    if (cachedUseCustomAuthUi !== null) {
        return Promise.resolve(cachedUseCustomAuthUi);
    }

    const sessionCachedValue: boolean | null = readCachedUseCustomAuthUiFromSession();
    if (sessionCachedValue !== null) {
        cachedUseCustomAuthUi = sessionCachedValue;
        return Promise.resolve(sessionCachedValue);
    }

    if (authClientConfigLoadPromise === null) {
        authClientConfigLoadPromise = fetchAuthClientConfigFromServer().finally(() => {
            authClientConfigLoadPromise = null;
        });
    }

    return authClientConfigLoadPromise;
};

const ensureAuthClientConfigLoaded = async (): Promise<boolean> => {
    return preloadAuthClientConfig();
};

const getCachedUseCustomAuthUiSync = (): boolean | null => {
    if (cachedUseCustomAuthUi !== null) {
        return cachedUseCustomAuthUi;
    }
    return readCachedUseCustomAuthUiFromSession();
};

const clearAuthClientConfigCache = (): void => {
    cachedUseCustomAuthUi = null;
    authClientConfigLoadPromise = null;
    try {
        sessionStorage.removeItem(AUTH_CLIENT_CONFIG_SESSION_CACHE_KEY);
    } catch {
        // ignore session storage failures
    }
};

export {
    AUTH_CLIENT_CONFIG_API_PATH,
    preloadAuthClientConfig,
    ensureAuthClientConfigLoaded,
    getCachedUseCustomAuthUiSync,
    clearAuthClientConfigCache,
};
